<template>
  <div class="header__mobile-menu">
    <button @click="toggleMenu" class="hamburger-button size-10 flex items-center justify-center" :aria-label="t('menu.toggle_menu_aria_label')" :aria-expanded="isMenuOpen">
      <svg v-if="!isMenuOpen" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M4 6H20V8H4V6ZM4 11H20V13H4V11ZM4 16H20V18H4V16Z" :fill="darkMode ? '#FFFFFF' : '#1A847B'" />
      </svg>
      <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
          d="M16.192 6.34424L11.949 10.5862L7.70697 6.34424L6.29297 7.75824L10.535 12.0002L6.29297 16.2422L7.70697 17.6562L11.949 13.4142L16.192 17.6562L17.606 16.2422L13.364 12.0002L17.606 7.75824L16.192 6.34424Z"
          :fill="darkMode ? '#FFFFFF' : '#1A847B'"
        />
      </svg>
    </button>

    <!-- Mobile Menu Drawer -->
    <Transition name="drawer">
      <div v-if="isMenuOpen" class="mobile-menu__overlay" @click="closeMenu">
        <div class="mobile-menu__drawer border-t border-t-white" :class="{ '!border-t-accent': !darkMode }" @click.stop>
          <NavigationMenuRoot v-model="opened" orientation="vertical" class="mobile-menu__navigation w-full" disablePointerLeaveClose disableHoverTrigger>
            <NavigationMenuList class="mobile-menu__list flex flex-col px-5 py-4 pace-y-0">
              <template v-for="(item, i) in menuItems" :key="i">
                <!-- Menu item without subitems -->
                <NavigationMenuItem v-if="!item.hasSubItems" class="border-b border-b-accent">
                  <NavigationMenuLink asChild>
                    <NuxtLink
                      :to="item.link.url"
                      :target="item.link.target"
                      :title="t('menu.navigateTo', { label: item.link.label })"
                      class="mobile-menu__link text-medium-semibold"
                      @click="closeMenu"
                    >
                      {{ item.link.label }}
                    </NuxtLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <!-- Menu item with subitems -->
                <NavigationMenuItem v-else class="border-b border-b-accent ">
                  <NavigationMenuTrigger class="mobile-menu__link mobile-menu__link--expandable" asChild>
                    <div role="button" class="mobile-menu__link-container w-full flex items-center justify-between" :class="[{ 'router-link-exact-active': isSubmenuActive(item) }]">
                      <NuxtLink
                        v-if="item.link.url"
                        :to="item.link.url"
                        :target="item.link.target"
                        :title="t('menu.navigateTo', { label: item.link.label })"
                        class="text-medium-semibold"
                        @click.stop="closeMenu"
                        >{{ item.link.label }}</NuxtLink
                      >
                      <span v-else class="text-medium-semibold">{{ item.link.label }}</span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="mobile-menu__caret">
                        <path d="M16.293 8.29297L12 12.586L7.70697 8.29297L6.29297 9.70697L12 15.414L17.707 9.70697L16.293 8.29297Z" fill="#1A847B" />
                      </svg>
                    </div>
                  </NavigationMenuTrigger>
                  <NavigationMenuContent class="mobile-menu__submenu">
                    <NavigationMenuSub>
                      <NavigationMenuList class="mobile-menu__submenu-list">
                        <NavigationMenuItem v-for="(subItem, j) in item.subItems" :key="`${i}-${j}`">
                          <NavigationMenuLink asChild>
                            <NuxtLink
                              :to="subItem.link.url"
                              :target="subItem.link.target"
                              :title="t('menu.navigateTo', { label: subItem.link.label })"
                              class="mobile-menu__link mobile-menu__link--sub text-regular-semibold"
                              @click="closeMenu"
                            >
                              {{ subItem.link.label }}
                            </NuxtLink>
                          </NavigationMenuLink>
                        </NavigationMenuItem>
                      </NavigationMenuList>
                    </NavigationMenuSub>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </template>
            </NavigationMenuList>
          </NavigationMenuRoot>
          <div class="mobile-menu__footer">
            <div class="mobile-menu__footer-item">
              <SharedButton label="Kontakt" to="/kontakt" size="small" class="w-full !py-[10px]" @click="closeMenu" />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
  import type { HeaderMenuItem } from './index.vue';

  import { NavigationMenuRoot, NavigationMenuList, NavigationMenuItem, NavigationMenuLink, NavigationMenuTrigger, NavigationMenuContent, NavigationMenuSub } from 'reka-ui';

  const { t } = useI18n();
  const { on: eventBusOn } = useEventBus();
  const route = useRoute();

  defineProps<{
    menuItems: HeaderMenuItem[];
    darkMode: boolean;
  }>();

  const isMenuOpen = ref(false);
  const opened = ref<string | undefined>(undefined);

  const { lockScroll, unlockScroll } = useScrollLock();

  const toggleMenu = () => {
    isMenuOpen.value = !isMenuOpen.value;
    if (isMenuOpen.value) {
      lockScroll();
    } else {
      unlockScroll();
      opened.value = undefined;
    }
  };

  const closeMenu = () => {
    isMenuOpen.value = false;
    unlockScroll();
    opened.value = undefined;
  };

  const handleEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && isMenuOpen.value) {
      closeMenu();
    }
  };
  // Close menu on escape key and listen for close events
  onMounted(() => {
    eventBusOn('site-logo-click', () => {
      if (isMenuOpen.value) {
        closeMenu();
      }
    });

    document.addEventListener('keydown', handleEscape);
  });

  onUnmounted(() => {
    document.removeEventListener('keydown', handleEscape);
    unlockScroll();
  });

  const isSubmenuActive = (item: HeaderMenuItem) => {
    if (!item.hasSubItems || !item.subItems) return false;
    return item.subItems.some(subItem => route.path === subItem.link.url);
  };
</script>

<style lang="scss" scoped>
  .mobile-menu {
    &__overlay {
      @apply fixed inset-0 bg-black bg-opacity-50 z-50;
      top: var(--header-height, 4rem); // Position below header
    }

    &__drawer {
      @apply fixed right-0 top-0 h-full w-full bg-white shadow-lg;
      top: var(--header-height, 4rem); // Position below header
      @apply flex flex-col;
      max-height: calc(100dvh - var(--header-height, 4rem));
      @screen md {
        @apply w-80;
      }
    }

    &__navigation {
      @apply flex overflow-y-auto;
    }

    &__footer {
      @apply mt-auto;
      @apply px-5 py-4;
    }

    &__link {
      @apply block w-full text-left px-2 py-3 text-primary transition-colors duration-150 hover:text-blue-whale-darker;
      @apply no-underline;
      @apply text-medium-semibold;

      &--expandable {
        @apply cursor-pointer;
      }

      &--sub {
        @apply pl-8;
        @apply border-b-0;
      }
    }

    &__link-container {
      @apply flex items-center gap-2 w-full;
    }

    &__caret {
      @apply transition-transform duration-150;
      transform-origin: center;
    }

    &__submenu-list {
      @apply flex flex-col gap-4 py-3;
      transition: all 0.2s ease-out;
    }

    &__list {
      &:deep(li) {
        @apply border-b border-b-accent;
      }
    }
  }

  // NavigationMenu component styling
  .mobile-menu__navigation {
    &:deep(> div) {
      @apply w-full;
    }
    &:deep(.navigation-menu-list) {
      @apply flex flex-col gap-0;
    }

    &:deep(.navigation-menu-trigger) {
      @apply w-full;
    }

    &:deep(.navigation-menu-content) {
      @apply overflow-hidden;
    }

    &:deep(.navigation-menu-sub) {
      @apply w-full;
    }

    &:deep(.navigation-menu-link) {
      @apply w-full;
    }

    // Rotate caret when submenu is open
    &:deep(.mobile-menu__link-container[data-state='open']) {
      .mobile-menu__caret {
        transform: rotate(180deg);
      }
    }

    // Submenu animations - using the same pattern as desktop menu
    &:deep(.mobile-menu__submenu) {
      @apply transition-all duration-150 ease-in-out;

      &[data-state='open'] {
        animation: enter 200ms ease-out;
      }

      &[data-state='closed'] {
        animation: exit 150ms ease-in;
      }
    }

    &:deep(.router-link-exact-active) {
      @apply underline;
    }
  }

  // Submenu animations
  @keyframes enter {
    0% {
      opacity: 0;
      transform: translateY(-8px);
    }
    50% {
      opacity: 0.5;
      transform: translateY(-4px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes exit {
    0% {
      opacity: 1;
      transform: translateY(0);
    }
    50% {
      opacity: 0.5;
      transform: translateY(-4px);
    }
    100% {
      opacity: 0;
      transform: translateY(-8px);
    }
  }

  // Drawer animations
  .drawer-enter-active,
  .drawer-leave-active {
    transition: all 0.3s ease;
  }

  .drawer-enter-from {
    opacity: 0;
  }

  .drawer-leave-to {
    opacity: 0;
  }

  .drawer-enter-active .mobile-menu__drawer,
  .drawer-leave-active .mobile-menu__drawer {
    transition: transform 0.3s ease;
  }

  .drawer-enter-from .mobile-menu__drawer {
    transform: translateX(100%);
  }

  .drawer-leave-to .mobile-menu__drawer {
    transform: translateX(100%);
  }
</style>
